/**
 * 頁 8 — 新增出庫單（`ui-spec` 頁 8；**只准職員／管理員**）
 *
 * 流程：揀類型（直銷／送貨）→ 揀去向（DB `destinations`，**唔准寫死**）→ 取貨時間／方式 →
 *       收貨方 ＋ 地址 → 逐項整箱出庫。建立完就去詳情頁（頁 9）發 WhatsApp 通知。
 *
 * 紅線（同後端一致；前端先擋係為咗好體驗，**後端才係權威**）
 *   · 去向類型要同出庫類型一致（`direct_sale` 對 `direct_sale`）；「剩貨去向」唔可以用落出庫單
 *   · 開單當時就要**有貨**（後端 `prepareLines({requireStock:true})`）—— 唔准開一張執唔到嘅單
 *   · 出庫一律**整箱**、箱數 ≥1 整數
 * ⚠️ cbm 一律後端算（`@coeldery/sku-schema`）—— 前端唔准自己乘。
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import FormSelectField from '@coeldery/form-select-field';
import FormTextField from '@coeldery/form-text-field';
import { btnGhost, btnPrimary, card, errText, hint as hintStyle } from '@coeldery/ui-kit';
import OutboundLinesEditor, { blankLine, type OutboundDraftLine } from '../components/OutboundLinesEditor';
import {
  DELIVERY_MODES,
  OUTBOUND_KINDS,
  destinationLabel,
  kindLabelKey,
  modeLabelKey,
} from '../components/OutboundStatusChip';
import {
  ApiError,
  api,
  type DeliveryMode,
  type Destination,
  type Item,
  type OutboundKind,
} from '../lib/api';

export default function OutboundNew() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [items, setItems] = useState<Item[]>([]);
  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [kind, setKind] = useState<OutboundKind>('direct_sale');
  const [destinationCode, setDestinationCode] = useState('');
  const [mode, setMode] = useState<DeliveryMode>('self_pickup');
  const [pickupAt, setPickupAt] = useState<string | null>(null);
  const [consigneeName, setConsigneeName] = useState<string | null>(null);
  const [consigneeAddress, setConsigneeAddress] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [lines, setLines] = useState<OutboundDraftLine[]>([blankLine()]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [itm, dest] = await Promise.all([api.items(), api.destinations()]);
    setItems(itm.items ?? []);
    /** 「剩貨去向」只喺場後結算用 → 呢度唔顯示（後端都會擋） */
    setDestinations((dest.destinations ?? []).filter((d) => d.kind !== 'disposition'));
  }, []);

  useEffect(() => {
    void load().catch((err: unknown) =>
      setError(err instanceof ApiError ? err.message : t('common.loadFailed')),
    );
  }, [load, t]);

  /** 只顯示同出庫類型一致嘅去向（類型一改，舊選擇要清走） */
  const destOptions = destinations.filter((d) => d.kind === kind);

  const changeKind = (next: OutboundKind) => {
    setKind(next);
    setDestinationCode('');
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!destinationCode) return setError(t('out.needDestination'));
    if (!pickupAt?.trim()) return setError(t('out.needPickupAt'));
    if (!consigneeName?.trim()) return setError(t('out.needConsignee'));

    const payload = lines
      .filter((l) => l.itemId && Number(l.cartons) >= 1)
      .map((l) => ({
        itemId: l.itemId,
        batchId: l.batchId || null,
        cartons: Number(l.cartons),
      }));
    if (!payload.length) return setError(t('out.needLine'));

    setBusy(true);
    try {
      const res = await api.createOutbound({
        outboundKind: kind,
        deliveryMode: mode,
        destinationCode,
        pickupAt: pickupAt.trim(),
        consigneeName: consigneeName.trim(),
        consigneeAddress: consigneeAddress?.trim() || null,
        note: note?.trim() || null,
        lines: payload,
      });
      if (res.order) navigate(`/outbound/${res.order.id}`);
      else navigate('/outbound');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section style={{ ...card, padding: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 800 }}>{t('out.newOrder')}</h2>
        <Link to="/outbound" style={btnGhost}>
          {t('out.back')}
        </Link>
      </div>
      <p style={hintStyle}>{t('out.newHint')}</p>

      <form onSubmit={submit} style={{ display: 'grid', gap: '12px', marginTop: '12px' }}>
        <FormSelectField
          id="f-ob-kind"
          labelKey="out.kind"
          value={kind}
          onChange={(v) => changeKind(v as OutboundKind)}
          options={OUTBOUND_KINDS.map((k) => ({ value: k as string, label: t(kindLabelKey(k)) }))}
        />
        <FormSelectField
          id="f-ob-destination"
          labelKey="out.destination"
          value={destinationCode}
          onChange={setDestinationCode}
          options={[
            { value: '', label: '—' },
            ...destOptions.map((d) => ({ value: d.code, label: destinationLabel(d, i18n.language) })),
          ]}
        />
        <FormSelectField
          id="f-ob-mode"
          labelKey="out.deliveryMode"
          value={mode}
          onChange={(v) => setMode(v as DeliveryMode)}
          options={DELIVERY_MODES.map((m) => ({ value: m as string, label: t(modeLabelKey(m)) }))}
        />
        <FormTextField
          id="f-ob-pickup"
          labelKey="out.pickupAt"
          type="datetime-local"
          value={pickupAt}
          onChange={setPickupAt}
          hintKey="out.hintPickupAt"
        />
        <FormTextField
          id="f-ob-consignee"
          labelKey="out.consignee"
          value={consigneeName}
          onChange={setConsigneeName}
        />
        <FormTextField
          id="f-ob-address"
          labelKey="out.address"
          value={consigneeAddress}
          onChange={setConsigneeAddress}
        />

        {items.length === 0 ? (
          <p style={hintStyle}>{t('out.noItems')}</p>
        ) : (
          <OutboundLinesEditor lines={lines} items={items} onChange={setLines} />
        )}

        <FormTextField id="f-ob-note" labelKey="out.note" value={note} onChange={setNote} />

        <button type="submit" style={btnPrimary} disabled={busy}>
          {busy ? t('common.saving') : t('out.create')}
        </button>
        {error && (
          <p style={errText} role="alert">
            {error}
          </p>
        )}
      </form>
    </section>
  );
}
